import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter, useDroppable, useDraggable,
} from "@dnd-kit/core";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ReferenceDot, BarChart, Bar } from "recharts";
import { Plus, Play, CheckCircle2, Rocket, Flag, Target, Activity, TrendingUp, Repeat, Trash2, AlertTriangle, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { toast } from "sonner";

const SPRINT_STATUS_META = {
  planned:   { label: "Planned",   cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  active:    { label: "Active",    cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  completed: { label: "Completed", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
  cancelled: { label: "Cancelled", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};
const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
const fmtDate = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";


/* ------------------- Create / Edit Sprint ------------------- */

function SprintForm({ open, onOpenChange, projectId, editing, onSaved, employees }) {
  const [form, setForm] = useState({ name: "", description: "", goal: "", start_date: "", end_date: "", capacity: 0, owner_id: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(editing
        ? { ...editing, owner_id: editing.owner_id || "" }
        : { name: "", description: "", goal: "", start_date: "", end_date: "", capacity: 0, owner_id: "" });
    }
  }, [open, editing]);

  const submit = async () => {
    if (!form.name || !form.start_date || !form.end_date) return toast.error("Name + dates are required");
    if (form.end_date < form.start_date) return toast.error("End date must be on or after start date");
    setSaving(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity) || 0 };
      if (!payload.owner_id) payload.owner_id = null;
      if (editing) await api.patch(`/sprints/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/sprints`, payload);
      toast.success(editing ? "Sprint updated" : "Sprint created");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="sprint-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Sprint" : "New Sprint"}</DialogTitle>
          <DialogDescription>Time-boxed execution period for this project.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input data-testid="sprint-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Goal</Label>
            <Input value={form.goal || ""} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="One-line sprint objective" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start *</Label><Input data-testid="sprint-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>End *</Label><Input data-testid="sprint-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Capacity (hours/points)</Label><Input data-testid="sprint-cap" type="number" min={0} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
            <div>
              <Label>Owner</Label>
              <Select value={form.owner_id || "none"} onValueChange={(v) => setForm({ ...form, owner_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="sprint-submit" onClick={submit} disabled={saving}>{saving ? "Saving…" : (editing ? "Save" : "Create sprint")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------- Carry-forward dialog ------------------- */

function CarryForwardDialog({ open, onOpenChange, sprint, allSprints, onDone }) {
  const [strategy, setStrategy] = useState("backlog");
  const [targetSprint, setTargetSprint] = useState("");
  const [saving, setSaving] = useState(false);
  // Only sprints of the same project, still planned, and starting on/after today — sorted by start_date.
  const todayIso = new Date().toISOString().slice(0, 10);
  const picks = allSprints
    .filter((s) => s.id !== sprint?.id && s.status === "planned" && s.project_id === sprint?.project_id)
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  const upcomingPlanned = picks.find((s) => (s.start_date || "") >= todayIso) || picks[0] || null;

  // Pre-select the next planned sprint whenever the dialog opens (fallback to backlog).
  useEffect(() => {
    if (!open) return;
    if (upcomingPlanned) {
      setStrategy("next_sprint");
      setTargetSprint(upcomingPlanned.id);
    } else {
      setStrategy("backlog");
      setTargetSprint("");
    }
    // eslint-disable-next-line
  }, [open, sprint?.id]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { strategy };
      if (strategy === "sprint") body.sprint_id = targetSprint;
      const { data } = await api.post(`/sprints/${sprint.id}/carry-forward`, body);
      toast.success(`${data.moved} task${data.moved === 1 ? "" : "s"} moved`);
      await api.patch(`/sprints/${sprint.id}/complete`);
      toast.success("Sprint completed");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="carryforward-dialog">
        <DialogHeader>
          <DialogTitle>Complete Sprint</DialogTitle>
          <DialogDescription>Decide what to do with incomplete tasks in <b>{sprint?.name}</b>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <label className="flex items-start gap-2 cursor-pointer glass rounded-md p-2.5">
            <input type="radio" name="cf" checked={strategy === "backlog"} onChange={() => setStrategy("backlog")} />
            <div><div className="font-medium">Move to Backlog</div><div className="text-xs text-muted-foreground">Default — safe and recommended.</div></div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer glass rounded-md p-2.5" data-testid="cf-next-sprint">
            <input type="radio" name="cf" checked={strategy === "next_sprint"} onChange={() => setStrategy("next_sprint")} />
            <div>
              <div className="font-medium">Move to next planned Sprint</div>
              <div className="text-xs text-muted-foreground">
                {upcomingPlanned ? (<>Will land in <b className="text-foreground">{upcomingPlanned.identifier} · {upcomingPlanned.name}</b>.</>) : "No planned sprint found — tasks go to Backlog."}
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer glass rounded-md p-2.5">
            <input type="radio" name="cf" checked={strategy === "sprint"} onChange={() => setStrategy("sprint")} />
            <div className="flex-1 min-w-0">
              <div className="font-medium">Move to a specific Sprint</div>
              {strategy === "sprint" && (
                <Select value={targetSprint} onValueChange={setTargetSprint}>
                  <SelectTrigger className="mt-2 h-8" data-testid="cf-sprint-sel"><SelectValue placeholder="Select sprint" /></SelectTrigger>
                  <SelectContent>{picks.map((s) => <SelectItem key={s.id} value={s.id}>{s.identifier} · {s.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer glass rounded-md p-2.5">
            <input type="radio" name="cf" checked={strategy === "stay"} onChange={() => setStrategy("stay")} />
            <div><div className="font-medium">Keep in completed Sprint</div><div className="text-xs text-muted-foreground">Historical record only — tasks won't roll into another sprint.</div></div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="cf-submit" onClick={submit} disabled={saving || (strategy === "sprint" && !targetSprint)}>
            {saving ? "Completing…" : "Complete Sprint"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------- Planning board (Backlog ↔ Sprint) ------------------- */

function TaskCard({ task, employees, listeners, attributes, setNodeRef, style, dragging }) {
  const assignee = employees.find((e) => e.id === task.assignee_id);
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, opacity: dragging ? 0.4 : 1 }}
      className="group glass rounded-lg px-2.5 py-2 flex items-center gap-2 cursor-grab active:cursor-grabbing hover:border-primary/40"
      {...attributes} {...listeners}
      data-testid={`sp-task-${task.id}`}
    >
      <Badge variant="outline" className="text-[9px] shrink-0 uppercase">{task.type || "task"}</Badge>
      <span className="flex-1 min-w-0 text-sm truncate">{task.title}</span>
      <Badge variant="outline" className="text-[10px] shrink-0 capitalize">{task.priority}</Badge>
      {task.estimated_hours > 0 && <span className="text-[10px] text-muted-foreground number">{task.estimated_hours}h</span>}
      {assignee && <Avatar className="h-5 w-5 shrink-0"><AvatarFallback className="text-[8px]">{initialsOf(assignee.full_name)}</AvatarFallback></Avatar>}
    </div>
  );
}

function DraggableTask({ task, employees }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id, data: task });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return <TaskCard task={task} employees={employees} setNodeRef={setNodeRef} style={style} attributes={attributes} listeners={listeners} dragging={isDragging} />;
}

function DropColumn({ id, title, subtitle, children, badge, 'data-testid': testid }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`glass rounded-xl p-3 flex flex-col min-h-[480px] ${isOver ? "ring-2 ring-primary/50" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
        <div>
          <div className="font-medium text-sm">{title}</div>
          <div className="text-[11px] text-muted-foreground">{subtitle}</div>
        </div>
        {badge}
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto max-h-[600px]">{children}</div>
    </div>
  );
}


function PlanningBoard({ sprint, projectId, onChanged, employees }) {
  const [backlog, setBacklog] = useState([]);
  const [sprintTasks, setSprintTasks] = useState([]);

  const reload = async () => {
    const [b, s] = await Promise.all([
      api.get(`/tasks/backlog/list?project_id=${projectId}`).then((r) => r.data),
      api.get(`/tasks?project_id=${projectId}&sprint_id=${sprint.id}`).then((r) => r.data),
    ]);
    setBacklog(b); setSprintTasks(s);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sprint.id, projectId]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = async (ev) => {
    const { active, over } = ev;
    if (!over) return;
    const taskId = active.id;
    const target = over.id;
    const inBacklog = backlog.find((t) => t.id === taskId);
    const inSprint = sprintTasks.find((t) => t.id === taskId);
    try {
      if (target === "sprint" && inBacklog) {
        await api.post(`/sprints/${sprint.id}/tasks/${taskId}`);
      } else if (target === "backlog" && inSprint) {
        await api.delete(`/sprints/${sprint.id}/tasks/${taskId}`);
      } else {
        return;
      }
      await reload();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  const sprintEstimate = sprintTasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);
  const over = sprint.capacity > 0 && sprintEstimate > sprint.capacity;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <div className="grid md:grid-cols-2 gap-4">
        <DropColumn
          id="backlog"
          title="Backlog"
          subtitle={`${backlog.length} task${backlog.length === 1 ? "" : "s"} available`}
          data-testid="plan-backlog"
          badge={<Badge variant="outline" className="text-[10px]">Drag →</Badge>}
        >
          {backlog.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">Backlog is empty.</div>
            : backlog.map((t) => <DraggableTask key={t.id} task={t} employees={employees} />)}
        </DropColumn>
        <DropColumn
          id="sprint"
          title={`${sprint.identifier} · ${sprint.name}`}
          subtitle={`${sprintTasks.length} task${sprintTasks.length === 1 ? "" : "s"} · ${sprintEstimate}h committed${sprint.capacity > 0 ? ` of ${sprint.capacity}h capacity` : ""}`}
          data-testid="plan-sprint"
          badge={over ? <Badge className="bg-red-500/20 text-red-400 border-red-500/40 text-[10px]">Over capacity</Badge> : null}
        >
          {sprintTasks.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">Drop tasks here from the backlog.</div>
            : sprintTasks.map((t) => <DraggableTask key={t.id} task={t} employees={employees} />)}
        </DropColumn>
      </div>
    </DndContext>
  );
}


/* ------------------- Sprint Dashboard (metrics + burndown) ------------------- */

function SprintDashboard({ sprint, projectId }) {
  const [metrics, setMetrics] = useState(sprint.metrics || {});
  const [burndown, setBurndown] = useState(null);
  const [velocity, setVelocity] = useState(null);

  useEffect(() => {
    api.get(`/sprints/${sprint.id}`).then((r) => setMetrics(r.data.metrics || {}));
    api.get(`/sprints/${sprint.id}/burndown`).then((r) => setBurndown(r.data));
    api.get(`/projects/${projectId}/velocity`).then((r) => setVelocity(r.data));
  }, [sprint.id, projectId]);

  const kpis = [
    { label: "Total Tasks",     value: metrics.total_tasks ?? 0,          color: "text-primary",      icon: Target },
    { label: "Completed",       value: metrics.completed_tasks ?? 0,      color: "text-emerald-500",  icon: CheckCircle2 },
    { label: "In Progress",     value: metrics.in_progress_tasks ?? 0,    color: "text-amber-500",    icon: Activity },
    { label: "Remaining",       value: metrics.remaining_estimate ?? 0,   color: "text-violet-500",   icon: TrendingUp },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sprint-kpis">
        {kpis.map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="glass rounded-xl p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg bg-background/50 flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="number font-medium text-xl leading-none mt-1">{value}</div></div>
          </div>
        ))}
      </div>

      <SectionCard
        title={`Burndown · ${burndown?.estimation_type || "hours"}`}
        subtitle={`Peak scope: ${burndown?.total_estimate ?? 0}${(burndown?.scope_events?.length || 0) > 0 ? ` · ${burndown.scope_events.length} scope change${burndown.scope_events.length === 1 ? "" : "s"}` : ""}`}
      >
        {burndown && burndown.series.length > 0 ? (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={burndown.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <Tooltip
                  contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const ev = (burndown.scope_events || []).find((e) => e.date === label);
                    return (
                      <div className="rounded-md border bg-card/95 backdrop-blur px-2.5 py-2 text-xs" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
                        <div className="font-medium mb-1">{label}</div>
                        {payload.map((p) => (
                          <div key={p.dataKey} className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
                            <span className="text-muted-foreground">{p.name}</span>
                            <span className="tabular-nums ml-auto">{p.value ?? "—"}</span>
                          </div>
                        ))}
                        {ev && (
                          <div className={`mt-1.5 pt-1.5 border-t text-[11px] flex items-center gap-1 ${ev.delta > 0 ? "text-amber-500" : "text-emerald-500"}`}>
                            <AlertTriangle className="w-3 h-3" />
                            Scope {ev.delta > 0 ? "added" : "removed"}: {ev.delta > 0 ? "+" : ""}{ev.delta}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="ideal" name="Ideal" stroke="#64748b" strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="actual" name="Actual" stroke="#10b981" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                <Line type="monotone" dataKey="scope" name="Scope" stroke="#f59e0b" strokeWidth={1} connectNulls dot={false} />
                {(burndown.scope_events || []).map((ev) => (
                  <ReferenceDot
                    key={ev.date}
                    x={ev.date}
                    y={ev.total_after}
                    r={6}
                    fill={ev.delta > 0 ? "#f59e0b" : "#10b981"}
                    stroke="var(--card)"
                    strokeWidth={2}
                    ifOverflow="extendDomain"
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-8 text-center">No daily snapshots yet. Start the sprint and progress to populate the chart.</div>
        )}

        {(burndown?.scope_events?.length || 0) > 0 && (
          <Collapsible className="mt-4" data-testid="scope-events-log">
            <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground group">
              <ChevronDown className="w-3.5 h-3.5 transition-transform group-data-[state=open]:rotate-180" />
              Scope change log ({burndown.scope_events.length})
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-1">
              {burndown.scope_events.map((ev) => (
                <div key={ev.date} className="glass rounded-md px-3 py-2 flex items-center gap-2 text-xs" data-testid={`scope-event-${ev.date}`}>
                  <span className="text-muted-foreground tabular-nums w-24 shrink-0">{ev.date}</span>
                  <AlertTriangle className={`w-3 h-3 shrink-0 ${ev.delta > 0 ? "text-amber-500" : "text-emerald-500"}`} />
                  <span className={`font-medium shrink-0 ${ev.delta > 0 ? "text-amber-500" : "text-emerald-500"}`}>
                    {ev.delta > 0 ? "+" : ""}{ev.delta} {burndown.estimation_type}
                  </span>
                  <span className="text-muted-foreground">
                    scope moved from <b className="text-foreground tabular-nums">{ev.total_before}</b> → <b className="text-foreground tabular-nums">{ev.total_after}</b>
                  </span>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </SectionCard>

      <SectionCard title="Velocity" subtitle={`Average over last ${velocity?.sprints?.length || 0} completed sprint(s): ${velocity?.average ?? 0}`}>
        {velocity && velocity.sprints.length > 0 ? (
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={velocity.sprints}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="identifier" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="completed_estimate" name="Completed" fill="#6366f1" />
                <ReferenceLine y={velocity.average} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: `Avg ${velocity.average}`, fontSize: 10, fill: "#f59e0b" }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-6 text-center">No completed sprints yet.</div>
        )}
      </SectionCard>
    </div>
  );
}


/* ------------------- Main Sprints page ------------------- */

export default function Sprints() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [params, setParams] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [sprints, setSprints] = useState([]);
  const [tab, setTab] = useState("list");
  const [activeSprint, setActiveSprint] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cfOpen, setCfOpen] = useState(false);
  const [cfSprint, setCfSprint] = useState(null);

  // Admins, or the manager of the selected project, may write sprints.
  const isAdmin = user?.role === "admin"
    || projects.some((p) => p.id === projectId && (p.manager_id === user?.id || (user?.role === "branch_admin" && p.created_by === user?.id)));

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (!projectId && r.data.length) setProjectId(r.data[0].id); });
    api.get("/employees").then((r) => setEmployees(r.data));
    // eslint-disable-next-line
  }, []);

  const reload = async () => {
    if (!projectId) return;
    const { data } = await api.get(`/projects/${projectId}/sprints`);
    setSprints(data);
    if (activeSprint) {
      const fresh = data.find((s) => s.id === activeSprint.id);
      if (fresh) setActiveSprint(fresh); else { setActiveSprint(null); setTab("list"); }
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  const summary = useMemo(() => ({
    planned: sprints.filter((s) => s.status === "planned").length,
    active:  sprints.filter((s) => s.status === "active").length,
    completed: sprints.filter((s) => s.status === "completed").length,
  }), [sprints]);

  const startSprint = async (s) => {
    try { await api.patch(`/sprints/${s.id}/start`); toast.success("Sprint started"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openCarry = (s) => { setCfSprint(s); setCfOpen(true); };
  const deleteSprint = async (s) => {
    if (!window.confirm(`Delete ${s.identifier}? Any tasks still inside will be sent to backlog.`)) return;
    try { await api.delete(`/sprints/${s.id}`); toast.success("Sprint deleted"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openSprintDetail = (s, nextTab = "plan") => { setActiveSprint(s); setTab(nextTab); };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="sprints-header" title="Sprints" subtitle="Plan, run, and review time-boxed delivery"
        actions={isAdmin && projectId && (
          <Button data-testid="new-sprint-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" />New Sprint
          </Button>
        )}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="h-9 w-64" data-testid="sprint-project"><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.code}</SelectItem>)}</SelectContent>
        </Select>
        <Badge variant="outline" className="text-[11px]">Planned: {summary.planned}</Badge>
        <Badge variant="outline" className="text-[11px] bg-emerald-500/10 text-emerald-400 border-emerald-500/30">Active: {summary.active}</Badge>
        <Badge variant="outline" className="text-[11px]">Completed: {summary.completed}</Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="sprint-tabs">
          <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan" disabled={!activeSprint}>Planning</TabsTrigger>
          <TabsTrigger value="dash" data-testid="tab-dash" disabled={!activeSprint}>Dashboard</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <SectionCard title={`${sprints.length} sprint${sprints.length === 1 ? "" : "s"}`} subtitle={projectId ? projects.find((p) => p.id === projectId)?.name : "Select a project"}>
            {sprints.length === 0 ? (
              <div className="text-sm text-muted-foreground py-10 text-center">No sprints yet. Create your first one.</div>
            ) : (
              <div className="space-y-2" data-testid="sprint-list">
                {sprints.map((s) => {
                  const sm = SPRINT_STATUS_META[s.status] || {};
                  const owner = employees.find((e) => e.id === s.owner_id);
                  return (
                    <div key={s.id} className="glass rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`sprint-row-${s.id}`}>
                      <Badge variant="outline" className="text-[10px] font-mono">{s.identifier}</Badge>
                      <div className="flex-1 min-w-0">
                        <button className="font-medium text-sm text-left hover:text-primary truncate block" onClick={() => openSprintDetail(s, "plan")}>{s.name}</button>
                        {s.goal && <div className="text-[11px] text-muted-foreground truncate">{s.goal}</div>}
                      </div>
                      <div className="text-[11px] text-muted-foreground number shrink-0"><Flag className="inline w-3 h-3 mr-1" />{fmtDate(s.start_date)} – {fmtDate(s.end_date)}</div>
                      {owner && <div className="flex items-center gap-1 text-[11px] shrink-0"><Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{initialsOf(owner.full_name)}</AvatarFallback></Avatar>{owner.full_name}</div>}
                      <Badge className={`${sm.cls} border text-[10px] shrink-0`}>{sm.label}</Badge>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="outline" data-testid={`sprint-plan-${s.id}`} onClick={() => openSprintDetail(s, "plan")}><Rocket className="w-3 h-3 mr-1" />Plan</Button>
                        <Button size="sm" variant="outline" data-testid={`sprint-dash-${s.id}`} onClick={() => openSprintDetail(s, "dash")}>Dashboard</Button>
                        {isAdmin && s.status === "planned" && (
                          <Button size="sm" data-testid={`sprint-start-${s.id}`} onClick={() => startSprint(s)}>
                            <Play className="w-3 h-3 mr-1" />Start
                          </Button>
                        )}
                        {isAdmin && s.status === "active" && (
                          <Button size="sm" data-testid={`sprint-complete-${s.id}`} onClick={() => openCarry(s)}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />Complete
                          </Button>
                        )}
                        {isAdmin && s.status !== "active" && (
                          <Button size="sm" variant="ghost" data-testid={`sprint-del-${s.id}`} onClick={() => deleteSprint(s)}>
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="plan" className="mt-4">
          {activeSprint && (
            <SectionCard title={`${activeSprint.identifier} · ${activeSprint.name}`} subtitle="Drag tasks between the Backlog and this Sprint">
              <PlanningBoard sprint={activeSprint} projectId={projectId} employees={employees} onChanged={reload} />
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="dash" className="mt-4">
          {activeSprint && <SprintDashboard sprint={activeSprint} projectId={projectId} />}
        </TabsContent>
      </Tabs>

      <SprintForm open={formOpen} onOpenChange={setFormOpen} projectId={projectId} editing={editing} employees={employees} onSaved={reload} />
      {cfSprint && <CarryForwardDialog open={cfOpen} onOpenChange={setCfOpen} sprint={cfSprint} allSprints={sprints} onDone={reload} />}
    </div>
  );
}
